/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";

const MEETING_PASSWORD_VERIFY_URL =
  "https://eoc.atlascrisis.com/ResetPasswordWithOtp/MobileAPI/meetings/verify";

export type MeetingPasswordVerificationResult =
  | "valid"
  | "invalid"
  | "unknown-room"
  | "unavailable";

function parseSuccessfulVerificationResponse(
  responseBody: string,
): MeetingPasswordVerificationResult {
  const normalizedBody = responseBody.trim().toLowerCase();
  if (!normalizedBody) return "valid";

  try {
    const json = JSON.parse(responseBody) as unknown;
    if (typeof json === "boolean") return json ? "valid" : "invalid";
    if (json && typeof json === "object") {
      const value = json as Record<string, unknown>;
      if (typeof value.isVerified === "boolean")
        return value.isVerified ? "valid" : "invalid";
      if (typeof value.valid === "boolean")
        return value.valid ? "valid" : "invalid";
      if (typeof value.success === "boolean")
        return value.success ? "valid" : "invalid";
    }
  } catch {
    // The backend currently documents text/plain; fall back to text parsing.
  }

  if (
    ["false", "invalid", "failed", "failure"].includes(normalizedBody) ||
    normalizedBody.includes("invalid") ||
    normalizedBody.includes("incorrect") ||
    normalizedBody.includes("wrong") ||
    normalizedBody.includes("not valid") ||
    normalizedBody.includes("not verified")
  )
    return "invalid";

  if (
    ["true", "valid", "success", "ok"].includes(normalizedBody) ||
    normalizedBody.includes("valid") ||
    normalizedBody.includes("success") ||
    normalizedBody.includes("verified")
  )
    return "valid";

  return "valid";
}

export async function verifyMeetingPassword(
  roomId: string,
  password: string,
  fetchFn: typeof fetch = globalThis.fetch,
): Promise<MeetingPasswordVerificationResult> {
  try {
    const response = await fetchFn(MEETING_PASSWORD_VERIFY_URL, {
      method: "POST",
      headers: {
        accept: "text/plain",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ roomId, password }),
    });

    if (response.ok)
      return parseSuccessfulVerificationResponse(await response.text());

    if ([400, 401, 403, 422].includes(response.status)) return "invalid";
    if (response.status === 404) return "unknown-room";

    logger.warn(
      `Meeting password verification failed with status ${response.status}`,
    );
    return "unavailable";
  } catch (error) {
    logger.error("Meeting password verification request failed", error);
    return "unavailable";
  }
}
